import { type CSSProperties, type PropsWithChildren, type Ref, useContext } from 'react';

import { Checkbox } from '@/components/Form';
import { Field, FieldLabel } from '@/components/Form/fields';
import { cn } from '@/lib/utils';

import { ListContext } from './List';

type Props = PropsWithChildren<{
  ref?: Ref<HTMLDivElement>;
  style?: CSSProperties;
  id: string;
  interactive?: boolean;
  disabled?: boolean;
}>;

const ListItem = ({ ref, style, id, interactive = true, disabled, children }: Props) => {
  const { value: selectedKeys, selectionMode, disabled: isListDisabled } = useContext(ListContext);

  const isChecked = selectedKeys?.includes(id) ?? false;
  const checkboxId = `list-item-checkbox-${id}`;
  const isDisabled = isListDisabled || disabled;

  return (
    <div
      ref={ref}
      role="listitem"
      className={cn(
        'flex items-center border-0 px-1.5 py-2 select-none',
        interactive && !isDisabled && 'hover:bg-gray-100',
      )}
      style={style}
    >
      <Field orientation="horizontal" data-disabled={isListDisabled || disabled}>
        {selectionMode !== 'none' && interactive && (
          <Checkbox
            id={checkboxId}
            value={id}
            checked={isChecked}
            disabled={isListDisabled || disabled}
          />
        )}
        <FieldLabel
          variant="small"
          htmlFor={selectionMode !== 'none' && interactive ? checkboxId : undefined}
        >
          {children}
        </FieldLabel>
      </Field>
    </div>
  );
};

export default ListItem;
